import { ExportProps } from 'features/report/report-constants'
import TableRowExport from 'component/Pdf/TableRowExport'
import TableRowCommon from 'component/Table/TableRowCommon'
import TableCustom from 'component/Table/TableCustom'
import _ from 'lodash'
import * as UseStyled from 'features/report/useStyled'
import { dateTimeToView } from 'utils/date.utils'
import { useTranslation } from 'react-i18next'
import { TableHead, TableSortLabel } from '@mui/material'
import moment from 'moment'
import { toBuddhistYear } from 'utils/app.utils'
import { WisdomToothSummary } from './WisdomTooth'

interface FcComponent {
  page: number
  pageLimit: number
  rowCount: number
  sortBy: string
  sortType: string
  data: any
  summary: WisdomToothSummary
  onRequestSort: (sortBy: string, sortType: string) => void
  handleChangeRowsPerPage: (val: any) => void
  handleChangePage: (val: any) => void
  componentRef: any
  exportData: any
  filter: any
}

export default function WisdomToothList(props: FcComponent) {
  const { t } = useTranslation()
  const headCells = [
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.#NO', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.DATE', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.NO_RECEIPT', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.CN', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.PATIENT_NAME', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.TREATMENT_RIGHT', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.GROUP', align: 'left', class: 'text-nowrap' }
  ]

  const renderData = (data: any, no: number, exportData: boolean) => {
    const num = exportData === true ? no + 1 : props.page * props.pageLimit - props.pageLimit + no + 1
    const groupLabel =
      data.ssoGroup === 'SSO'
        ? t('REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.SSO_GROUP')
        : t('REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.NON_SSO_GROUP')

    const objRenderData = {
      key: num.toString(),
      id: no.toString(),
      obj: data,
      columns: [
        { option: 'TEXT', align: 'left', label: num },
        { option: 'TEXT', align: 'left', label: dateTimeToView(data.paidDateTime), class: 'text-nowrap' },
        { option: 'TEXT', align: 'left', label: data.billNo, class: 'text-nowrap' },
        { option: 'TEXT', align: 'left', label: data.cnNumber || '-' },
        { option: 'TEXT', align: 'left', label: data.patientFullname },
        { option: 'TEXT', align: 'left', label: data.treatmentRightName || '-' },
        { option: 'TEXT', align: 'left', label: groupLabel }
      ]
    }
    return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
  }

  return (
    <>
      <UseStyled.ResponsiveReportTable>
        <TableCustom
          page={props.page}
          pageLimit={props.pageLimit}
          sortType={props.sortType}
          sortBy={props.sortBy}
          rowCount={props.rowCount}
          onSort={props.onRequestSort}
          setPageLimit={props.handleChangeRowsPerPage}
          setPage={props.handleChangePage}
          headCells={headCells}
          rowsData={_.map(props.data, (d: any, index: number) => renderData(d, index, false))}
          customScroll
        />
      </UseStyled.ResponsiveReportTable>
      <div className="print-show" ref={props.componentRef}>
        <WisdomToothExport
          filter={props.filter}
          headCells={headCells}
          rowsData={_.map(props.exportData, (d: any, index: number) => renderData(d, index, true))}
          summary={props.summary}
        />
      </div>
    </>
  )
}

interface WisdomToothExportProps extends ExportProps {
  summary: WisdomToothSummary
}

export function WisdomToothExport(props: WisdomToothExportProps) {
  const { t } = useTranslation()
  const head = props.headCells
  const headCells = head.filter((headCell: any) => headCell.id !== 'action')

  const FILTER_TITLES = [
    { key: 'BRANCH', label: 'REPORT.FILTER.BRANCH' },
    { key: 'DATE_START', label: 'REPORT.FILTER.DATE_START' },
    { key: 'DATE_END', label: 'REPORT.FILTER.DATE_END' }
  ]

  const renderValue = (value: any, index: any) => {
    if (index === 1 || index === 2) {
      if (value) return toBuddhistYear(moment(value), 'DD/MM/YYYY')
      return '-'
    }
    return value
  }

  return (
    <>
      <TableHead style={{ fontWeight: 800 }}>{t('REPORT.REPORT_NAME.WISDOM_TOOTH_EXTRACTION')}</TableHead>
      <table>
        <tr>
          {FILTER_TITLES.map((filter, index) => (
            <TableSortLabel key={index}>
              <td className="mr-2" style={{ fontSize: 10, fontWeight: 800 }}>
                {t(filter.label)} :{' '}
              </td>
              <td style={{ fontSize: 10, fontWeight: 500 }}>{renderValue(props.filter[index], index)}</td>
            </TableSortLabel>
          ))}
        </tr>
      </table>
      <table style={{ marginTop: 8 }}>
        <tr>
          <td style={{ fontSize: 10, fontWeight: 800, paddingRight: 12 }}>
            {t('REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.SSO_GROUP')}:
          </td>
          <td style={{ fontSize: 10, paddingRight: 24 }}>{props.summary?.sso ?? 0}</td>
          <td style={{ fontSize: 10, fontWeight: 800, paddingRight: 12 }}>
            {t('REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.NON_SSO_GROUP')}:
          </td>
          <td style={{ fontSize: 10, paddingRight: 24 }}>{props.summary?.nonSso ?? 0}</td>
          <td style={{ fontSize: 10, fontWeight: 800, paddingRight: 12 }}>
            {t('REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.TOTAL')}:
          </td>
          <td style={{ fontSize: 10 }}>{props.summary?.total ?? 0}</td>
        </tr>
      </table>
      <UseStyled.Styles className="pt-3">
        <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', minWidth: '100%', maxWidth: '100%' }}>
          <thead>
            <tr>
              {_.map(headCells, (val: any) => (
                <td style={{ textAlign: val.align, fontSize: 10, fontWeight: 500 }}> {t(val.labelKey)}</td>
              ))}
            </tr>
          </thead>
          <tbody>
            {props.rowsData}
            {!props.rowsData.length && (
              <td colSpan={headCells.length} style={{ fontSize: 10 }}>
                {t('REPORT.TITLE.NO_DATA')}
              </td>
            )}
            <tr>
              <td colSpan={headCells.length - 1} style={{ fontSize: 10, fontWeight: 800, textAlign: 'right', paddingTop: 8 }}>
                {t('REPORT.TABLE.WISDOM_TOOTH_EXTRACTION.TOTAL')}
              </td>
              <td style={{ fontSize: 10, fontWeight: 800, paddingTop: 8 }}>{props.summary?.total ?? 0}</td>
            </tr>
          </tbody>
        </table>
      </UseStyled.Styles>
    </>
  )
}
